import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../layouts/Layout';
import homeIcon from '../assets/img3d/home.png';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from '../hooks/use-toast';
import { Plus, X, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import FileUpload from '../components/ui/FileUpload';
import FilePreview from '../components/ui/FilePreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddPropertyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [amenity, setAmenity] = useState('');

  // Créneaux de visite
  const [visitSlots, setVisitSlots] = useState([]);
  const [slotDraft, setSlotDraft] = useState({
    dayOfWeek: '',
    start: '',
    end: '',
    duration: 60,
    isActive: true,
  });
  
  // État pour gérer l'ouverture/fermeture des sections
  const [openSections, setOpenSections] = useState({
    general: true,      // Informations générales (dépliée par défaut)
    location: false,     // Localisation
    characteristics: false, // Caractéristiques et équipements
    photos: false      // Photos
  });

  const [buildings, setBuildings] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: '',
    transaction_type: '',
    price: '',
    location: '',
    city: '',
    postal_code: '',
    surface_area: '',
    rooms: '',
    bedrooms: '',
    bathrooms: '',
    floor: '',
    total_floors: '',
    has_multiple_levels: false,
    property_levels: '',
    building_id: '',
    building_name: '',
    year_built: '',
    energy_rating: '',
    amenities: [],
    featured: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const timeToMinutes = (value) => {
    if (!value) return null;
    const [h, m] = value.split(':').map((v) => parseInt(v, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const addSlot = () => {
    const { dayOfWeek, start, end, duration, isActive } = slotDraft;
    if (dayOfWeek === '' || !start || !end) return;
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);
    if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) return;
    setVisitSlots((prev) => [
      ...prev,
      {
        dayOfWeek: parseInt(dayOfWeek, 10),
        startMinutes,
        endMinutes,
        duration: duration ? parseInt(duration, 10) : 60,
        isActive,
        id: crypto.randomUUID(),
        label: `${['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'][dayOfWeek]} ${start} - ${end}`,
      },
    ]);
    setSlotDraft({ dayOfWeek: '', start: '', end: '', duration: 60, isActive: true });
  };

  const removeSlot = (id) => {
    setVisitSlots((prev) => prev.filter((s) => s.id !== id));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePropertyTypeChange = (value) => {
    setFormData(prev => {
      const next = {
        ...prev,
        property_type: value,
      };

      // If the property is not a unit in a building, clear building floor info
      if (!isBuildingUnit(value)) {
        next.floor = '';
        next.total_floors = '';
        next.building_id = '';
        next.building_name = '';
      }

      // For very simple types in the future we could also reset internal levels if needed
      return next;
    });
  };

  const isBuildingUnit = (propertyType) => {
    return ['apartment', 'studio', 'loft'].includes(propertyType);
  };

  const supportsInternalLevels = (propertyType) => {
    // Any habitable property can conceptually span multiple levels
    return ['apartment', 'studio', 'loft', 'house', 'villa'].includes(propertyType);
  };

  // Load buildings for current professional when a building-eligible type is chosen
  useEffect(() => {
    const shouldLoadBuildings = isBuildingUnit(formData.property_type);
    if (!shouldLoadBuildings) {
      return;
    }

    const fetchBuildings = async () => {
      try {
        const response = await axios.get(`${API}/professional/buildings`);
        setBuildings(response.data || []);
      } catch (error) {
        console.error('Error loading buildings', error);
      }
    };

    fetchBuildings();
  }, [formData.property_type]);

  const handleFilesUpload = (files) => {
    setUploadedFiles(files);
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const addAmenity = () => {
    if (amenity && !formData.amenities.includes(amenity)) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, amenity],
      }));
      setAmenity('');
    }
  };

  const removeAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload des fichiers d'abord
      let imageUrls = [];
      if (uploadedFiles.length > 0) {
        const formDataUpload = new FormData();
        uploadedFiles.forEach(file => {
          formDataUpload.append('files', file.file);
        });

        try {
          const uploadResponse = await axios.post(`${API}/upload/images`, formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          imageUrls = uploadResponse.data.urls || [];
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          toast({
            title: 'Erreur d\'upload',
            description: uploadError.response?.data?.message || 'Impossible d\'uploader les images',
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
      }

      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        surface_area: parseFloat(formData.surface_area),
        rooms: parseInt(formData.rooms),
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        floor: formData.floor ? parseInt(formData.floor) : null,
        total_floors: formData.total_floors ? parseInt(formData.total_floors) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        has_multiple_levels: !!formData.has_multiple_levels,
        property_levels:
          formData.has_multiple_levels && formData.property_levels
            ? parseInt(formData.property_levels)
            : null,
        building_id: formData.building_id || undefined,
        building_name: formData.building_name || undefined,
        images: imageUrls, // Utiliser les URLs des fichiers uploadés
        visitSlots: visitSlots.map((slot) => ({
          dayOfWeek: slot.dayOfWeek,
          startMinutes: slot.startMinutes,
          endMinutes: slot.endMinutes,
          duration: slot.duration,
          isActive: slot.isActive,
        })),
      };

      const response = await axios.post(`${API}/properties`, submitData);
      
      toast({
        title: 'Annonce créée',
        description: 'Votre bien a été ajouté avec succès',
      });

      navigate(`/properties/${response.data.id}`);
    } catch (error) {
      console.error('Error creating property:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.message || 'Impossible de créer l\'annonce',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout
      headerConfig={{
        icon: homeIcon,
        iconColor: 'bg-white',
        title: 'Déposer une annonce',
        subtitle: 'Remplissez les informations de votre bien immobilier',
        primaryAction: 'Retour',
        secondaryAction: 'Mes Annonces',
        onPrimaryAction: () => navigate(-1),
        onSecondaryAction: () => navigate('/dashboard')
      }}
    >
      <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Information */}
              <div className="border rounded-lg p-4 bg-white">
                <button
                  type="button"
                  onClick={() => toggleSection('general')}
                  className="w-full flex items-center justify-between text-xl font-bold text-gray-900 mb-4 hover:text-[#FF9F68] transition-colors"
                >
                  <span>Informations générales</span>
                  {openSections.general ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
                {openSections.general && (
                  <div className="space-y-4 transition-all duration-200">
                  <div>
                    <Label htmlFor="title">Titre de l'annonce *</Label>
                    <Input
                      id="title"
                      placeholder="Ex: Bel appartement 3 pièces avec balcon"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      rows={5}
                      placeholder="Décrivez votre bien en détail..."
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Type de transaction *</Label>
                      <Select
                        value={formData.transaction_type}
                        onValueChange={(value) => handleChange('transaction_type', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sale">Vente</SelectItem>
                          <SelectItem value="rent">Location</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Type de bien *</Label>
                      <Select
                        value={formData.property_type}
                        onValueChange={handlePropertyTypeChange}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="apartment">Appartement</SelectItem>
                          <SelectItem value="house">Maison</SelectItem>
                          <SelectItem value="studio">Studio</SelectItem>
                          <SelectItem value="loft">Loft</SelectItem>
                          <SelectItem value="villa">Villa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="price">Prix*</Label>
                      <Input
                        id="price"
                        type="number"
                        placeholder="250000"
                        value={formData.price}
                        onChange={(e) => handleChange('price', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  </div>
                )}
              </div>

              {/* Location */}
              <div className="border rounded-lg p-4 bg-white">
                <button
                  type="button"
                  onClick={() => toggleSection('location')}
                  className="w-full flex items-center justify-between text-xl font-bold text-gray-900 mb-4 hover:text-[#FF9F68] transition-colors"
                >
                  <span>Localisation</span>
                  {openSections.location ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
                {openSections.location && (
                  <div className="space-y-4 transition-all duration-200">
                    {isBuildingUnit(formData.property_type) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="building_name">Immeuble</Label>
                          <Input
                            id="building_name"
                            placeholder="Ex: Résidence Les Jardins"
                            list="building-options"
                            value={formData.building_name}
                            onChange={(e) => {
                              const name = e.target.value;
                              handleChange('building_name', name);
                              const matching = buildings.find(
                                (b) => b.name.toLowerCase() === name.toLowerCase()
                              );
                              if (matching) {
                                handleChange('building_id', matching.id);
                                handleChange('location', matching.location || '');
                                handleChange('city', matching.city || '');
                                handleChange('postal_code', matching.postalCode || '');
                              } else {
                                handleChange('building_id', '');
                              }
                            }}
                          />
                          <datalist id="building-options">
                            {buildings.map((b) => (
                              <option key={b.id} value={b.name} />
                            ))}
                          </datalist>
                          <p className="mt-1 text-xs text-gray-500">
                            Choisissez un immeuble existant ou saisissez un nouveau nom.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="location">Adresse *</Label>
                        <Input
                          id="location"
                          placeholder="123 Rue de la République"
                          value={formData.location}
                          onChange={(e) => handleChange('location', e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="city">Ville *</Label>
                        <Input
                          id="city"
                          placeholder="Abidjan"
                          value={formData.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="postal_code">Code postal *</Label>
                        <Input
                          id="postal_code"
                          placeholder="75001"
                          value={formData.postal_code}
                          onChange={(e) => handleChange('postal_code', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

                {/* Property Details and Amenities */}
                <div className="border rounded-lg p-4 bg-white">
                <button
                  type="button"
                  onClick={() => toggleSection('characteristics')}
                  className="w-full flex items-center justify-between text-xl font-bold text-gray-900 mb-4 hover:text-[#FF9F68] transition-colors"
                >
                  <span>Caractéristiques et équipements</span>
                  {openSections.characteristics ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
                {openSections.characteristics && (
                  <div className="space-y-6 transition-all duration-200">
                  {/* Property Details */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="surface_area">Surface (m²) *</Label>
                      <Input
                        id="surface_area"
                        type="number"
                        placeholder="75"
                        value={formData.surface_area}
                        onChange={(e) => handleChange('surface_area', e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="rooms">Pièces *</Label>
                      <Input
                        id="rooms"
                        type="number"
                        placeholder="3"
                        value={formData.rooms}
                        onChange={(e) => handleChange('rooms', e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="bedrooms">Chambres *</Label>
                      <Input
                        id="bedrooms"
                        type="number"
                        placeholder="2"
                        value={formData.bedrooms}
                        onChange={(e) => handleChange('bedrooms', e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="bathrooms">Salles de bain *</Label>
                      <Input
                        id="bathrooms"
                        type="number"
                        placeholder="1"
                        value={formData.bathrooms}
                        onChange={(e) => handleChange('bathrooms', e.target.value)}
                        required
                      />
                    </div>

                    {isBuildingUnit(formData.property_type) && (
                      <>
                        <div>
                          <Label htmlFor="floor">
                            Étage du bien
                          </Label>
                          <Input
                            id="floor"
                            type="number"
                            min={0}
                            placeholder="3"
                            value={formData.floor}
                            onChange={(e) => handleChange('floor', e.target.value)}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Niveau où se situe l’appartement dans l’immeuble.
                          </p>
                        </div>

                        <div>
                          <Label htmlFor="total_floors">
                            Étages de l’immeuble
                          </Label>
                          <Input
                            id="total_floors"
                            type="number"
                            min={1}
                            placeholder="5"
                            value={formData.total_floors}
                            onChange={(e) => handleChange('total_floors', e.target.value)}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Nombre total de niveaux de l’immeuble (RDC compris).
                          </p>
                        </div>
                      </>
                    )}

                    {supportsInternalLevels(formData.property_type) && (
                      <>
                        <div className="md:col-span-3 flex items-center justify-between rounded-md border px-3 py-2">
                          <div>
                            <Label className="block">
                              Bien sur plusieurs niveaux ?
                            </Label>
                            <p className="mt-1 text-xs text-gray-500">
                              Indiquez si le bien est un duplex, triplex, ou possède plusieurs niveaux.
                            </p>
                          </div>
                          <Switch
                            checked={!!formData.has_multiple_levels}
                            onCheckedChange={(checked) =>
                              handleChange('has_multiple_levels', checked)
                            }
                            aria-label="Bien sur plusieurs niveaux"
                          />
                        </div>

                        {formData.has_multiple_levels && (
                          <div>
                            <Label htmlFor="property_levels">
                              Nombre de niveaux du bien
                            </Label>
                            <Input
                              id="property_levels"
                              type="number"
                              min={1}
                              placeholder="2"
                              value={formData.property_levels}
                              onChange={(e) => handleChange('property_levels', e.target.value)}
                              required={!!formData.has_multiple_levels}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Exemple : 1 pour plain-pied, 2 pour duplex, 3 pour triplex.
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    <div>
                      <Label htmlFor="year_built">Année de construction</Label>
                      <Input
                        id="year_built"
                        type="number"
                        placeholder="2010"
                        value={formData.year_built}
                        onChange={(e) => handleChange('year_built', e.target.value)}
                      />
                    </div>

                    {/* <div>
                      <Label htmlFor="energy_rating">DPE</Label>
                      <Select
                        value={formData.energy_rating}
                        onValueChange={(value) => handleChange('energy_rating', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A">A</SelectItem>
                          <SelectItem value="B">B</SelectItem>
                          <SelectItem value="C">C</SelectItem>
                          <SelectItem value="D">D</SelectItem>
                          <SelectItem value="E">E</SelectItem>
                          <SelectItem value="F">F</SelectItem>
                          <SelectItem value="G">G</SelectItem>
                        </SelectContent>
                      </Select>
                    </div> */}
                  </div>

                  {/* Amenities */}
                  <div className="pt-4 border-t">
                    <Label className="text-base font-semibold mb-3 block">Équipements</Label>
                    <div className="space-y-4">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Ex: Parking, Ascenseur, Balcon..."
                          value={amenity}
                          onChange={(e) => setAmenity(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                        />
                        <Button type="button" onClick={addAmenity} className="bg-[#FF9F68] hover:bg-[#E67E4F]">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      {formData.amenities.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formData.amenities.map((item, index) => (
                            <Badge key={index} variant="secondary" className="pr-1">
                              {item}
                              <button
                                type="button"
                                onClick={() => removeAmenity(index)}
                                className="ml-2 hover:text-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                )}
              </div>

              {/* Images */}
              {/* Visit slots */}
              <div className="border rounded-lg p-4 bg-white">
                <button
                  type="button"
                  onClick={() => toggleSection('slots')}
                  className="w-full flex items-center justify-between text-xl font-bold text-gray-900 mb-4 hover:text-[#FF9F68] transition-colors"
                >
                  <span>Créneaux de visite</span>
                  {openSections.slots ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
                {openSections.slots && (
                  <div className="space-y-4 transition-all duration-200">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                      <div>
                        <Label>Jour</Label>
                        <Select
                          value={slotDraft.dayOfWeek}
                          onValueChange={(value) => setSlotDraft((p) => ({ ...p, dayOfWeek: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Lundi</SelectItem>
                            <SelectItem value="2">Mardi</SelectItem>
                            <SelectItem value="3">Mercredi</SelectItem>
                            <SelectItem value="4">Jeudi</SelectItem>
                            <SelectItem value="5">Vendredi</SelectItem>
                            <SelectItem value="6">Samedi</SelectItem>
                            <SelectItem value="0">Dimanche</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Début</Label>
                        <Input
                          type="time"
                          value={slotDraft.start}
                          onChange={(e) => setSlotDraft((p) => ({ ...p, start: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Fin</Label>
                        <Input
                          type="time"
                          value={slotDraft.end}
                          onChange={(e) => setSlotDraft((p) => ({ ...p, end: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Durée (min)</Label>
                        <Input
                          type="number"
                          min={5}
                          max={480}
                          value={slotDraft.duration}
                          onChange={(e) => setSlotDraft((p) => ({ ...p, duration: e.target.value }))}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={slotDraft.isActive}
                            onCheckedChange={(checked) => setSlotDraft((p) => ({ ...p, isActive: checked }))}
                            aria-label="Créneau actif"
                          />
                          <span className="text-sm text-gray-700">Actif</span>
                        </div>
                        <Button type="button" onClick={addSlot} className="bg-[#FF9F68] hover:bg-[#E67E4F]">
                          Ajouter
                        </Button>
                      </div>
                    </div>

                    {visitSlots.length > 0 && (
                      <div className="border rounded-lg divide-y">
                        {visitSlots.map((slot) => (
                          <div key={slot.id} className="flex items-center justify-between px-3 py-2">
                            <div className="text-sm text-gray-700">
                              {slot.label} — {slot.duration} min {slot.isActive ? '' : '(inactif)'}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSlot(slot.id)}
                              className="text-red-500 hover:text-red-600 text-sm"
                            >
                              Supprimer
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {visitSlots.length === 0 && (
                      <p className="text-sm text-gray-500">Aucun créneau ajouté pour l’instant.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Images */}
              <div className="border rounded-lg p-4 bg-white">
                <button
                  type="button"
                  onClick={() => toggleSection('photos')}
                  className="w-full flex items-center justify-between text-xl font-bold text-gray-900 mb-4 hover:text-[#FF9F68] transition-colors"
                >
                  <span>Photos</span>
                  {openSections.photos ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
                {openSections.photos && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-200">
                  <div className="w-full">
                    <FileUpload
                      onFilesUpload={handleFilesUpload}
                      acceptedTypes={{ 'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif'] }}
                      maxSize={5 * 1024 * 1024} // 5MB
                      maxFiles={10}
                      fileTypeDisplay="JPG, PNG, WEBP, GIF"
                    />
                  </div>
                  <div className="w-full">
                    <FilePreview
                      files={uploadedFiles}
                      onRemoveFile={handleRemoveFile}
                    />
                  </div>
                  </div>
                )}
              </div>

              {/* Seller Information */}

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-6 border-t justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#FF9F68] hover:bg-[#E67E4F] text-white font-semibold"
                >
                  {loading ? 'Publication...' : 'Publier l\'annonce'}
                </Button>
              </div>
            </form>
    </Layout>
  );
};

export default AddPropertyPage;
